import { useState } from "react";
import styled from "styled-components";
import { FiDownload, FiMaximize2, FiRotateCw } from "react-icons/fi";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { IconButton } from "../../components/IconButton";
import { Paragraph } from "../../components/Paragraph";
import { Image as ImageType } from "../../types/Individual";
import {
  normaliseSide,
  QualityCheck,
  QualityCheckList,
} from "./ElevatedIdResult";
import { Divider, SubHeading } from "./IdLivenessPrimitives";
import {
  downloadBase64Image,
  ImageViewer,
  RotatableImage,
  ThumbButton,
  useImageRotations,
  ViewableImage,
} from "./ImageViewer";

/**
 * Two equal halves, front and back side by side; a single side takes one.
 * Each side's quality checks, title and image sit in their own grid rows, so
 * the two images line up even when one side has more checks than the other.
 * The row gap is small so the title sits tight to its image; the checks
 * carry their own space below.
 */
const SideGrid = styled.div`
  ${({ theme: { space } }) => `
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: ${space[4]};
    row-gap: ${space[1]};
  `}
`;

const Cell = styled.div<{ $column: number; $row: number }>`
  grid-column: ${({ $column }) => $column};
  grid-row: ${({ $row }) => $row};
  min-width: 0;
`;

const Placeholder = styled.div`
  ${({ theme: { colors, radii } }) => `
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.gray[5]};
    border: 1px solid ${colors.gray[100]};
    border-radius: ${radii[0]};
  `}
`;

type Side = ViewableImage & { side: "front" | "back" | null };

/**
 * The Document Images part of the identity document card: front and back
 * side by side, each filling half the card, each with the provider's image
 * quality checks for that side above its title. Clicking an image opens it full
 * size. A one-sided document shows only the front.
 */
export const DocumentImages = ({
  images,
  base64Images,
  qualityChecks,
}: {
  images?: ImageType[];
  base64Images?: string[];
  qualityChecks: QualityCheck[];
}) => {
  const { rotationOf, rotate } = useImageRotations();
  const [viewing, setViewing] = useState<string | null>(null);

  // The label and side come from the image's own name, not its position,
  // so a back-only capture is not labelled as the front.
  const sides: Side[] = (images ?? []).map((image, i) => {
    const side = normaliseSide(image?.name) ?? (i === 0 ? "front" : "back");
    return {
      key: `${side}-${i}`,
      label: side === "back" ? "Back of document" : "Front of document",
      downloadName: side === "back" ? "back.jpg" : "front.jpg",
      side,
      base64: base64Images?.[i],
    };
  });

  const shownSides = new Set(sides.map((s) => s.side));
  const checksFor = (side: Side["side"]) =>
    qualityChecks.filter(
      (check) => normaliseSide(check.document_side) === side
    );
  const otherChecks = qualityChecks.filter(
    (check) => !shownSides.has(normaliseSide(check.document_side))
  );

  if (!sides.length && !qualityChecks.length) return null;

  const viewed = sides.find((s) => s.key === viewing);
  // With no checks on any shown side, drop the checks row so its gaps go too.
  const hasSideChecks = sides.some((s) => checksFor(s.side).length > 0);
  const rowsPerBand = hasSideChecks ? 3 : 2;

  return (
    <>
      <Divider />
      <SubHeading>Document Images</SubHeading>

      {sides.length > 0 && (
        <SideGrid>
          {sides.map((item, i) => {
            // Two sides to a band of rows: checks (if any), then the image's
            // title and buttons directly above the image.
            const column = (i % 2) + 1;
            const row = Math.floor(i / 2) * rowsPerBand + 1;
            const imageRow = row + rowsPerBand - 1;
            const checks = checksFor(item.side);
            return [
              checks.length > 0 && (
                <Cell key={`${item.key}-checks`} $column={column} $row={row}>
                  <Box mb={2}>
                    <Paragraph m="0px" mb={1} fontWeight="600" color="gray.80">
                      {item.side === "back" ? "Back" : "Front"} image quality
                      checks
                    </Paragraph>
                    <QualityCheckList checks={checks} />
                  </Box>
                </Cell>
              ),
              <Cell
                key={`${item.key}-title`}
                $column={column}
                $row={imageRow - 1}
              >
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  gap="8"
                >
                  <Paragraph m="0px" fontWeight="600" color="gray.80">
                    {item.label}
                  </Paragraph>
                  {item.base64 && (
                    <Flex gap="4" alignItems="center">
                      <IconButton
                        Type={FiRotateCw}
                        size="16px"
                        title="Rotate"
                        onClick={() => rotate(item.key)}
                      />
                      <IconButton
                        Type={FiDownload}
                        size="16px"
                        title="Download"
                        onClick={() =>
                          downloadBase64Image(item.base64, item.downloadName)
                        }
                      />
                      <IconButton
                        Type={FiMaximize2}
                        size="16px"
                        title="View full size"
                        onClick={() => setViewing(item.key)}
                      />
                    </Flex>
                  )}
                </Flex>
              </Cell>,
              <Cell key={`${item.key}-image`} $column={column} $row={imageRow}>
                {item.base64 ? (
                  <ThumbButton
                    type="button"
                    aria-label={`View ${item.label.toLowerCase()} full size`}
                    onClick={() => setViewing(item.key)}
                  >
                    <RotatableImage
                      base64={item.base64}
                      alt={item.label}
                      rotation={rotationOf(item.key)}
                      maxHeight={520}
                    />
                  </ThumbButton>
                ) : (
                  <Placeholder>
                    <Paragraph m="0px" fontSize={1} color="gray.60">
                      {base64Images ? "Image not available" : "Loading image"}
                    </Paragraph>
                  </Placeholder>
                )}
              </Cell>,
            ];
          })}
        </SideGrid>
      )}

      {otherChecks.length > 0 && (
        <Box mt={sides.length ? 3 : 0}>
          {sides.length > 0 && (
            <Paragraph m="0px" mb={1} fontWeight="600" color="gray.80">
              Other image quality checks
            </Paragraph>
          )}
          <QualityCheckList checks={otherChecks} />
        </Box>
      )}

      <ImageViewer
        image={viewed}
        rotation={viewed ? rotationOf(viewed.key) : 0}
        onRotate={() => viewed && rotate(viewed.key)}
        onClose={() => setViewing(null)}
      />
    </>
  );
};
